/**
 * DispositionModal — V2.1.0 (Feb 2026, Phase 1)
 *
 * Dual-mode disposition picker used across the CRM list, the CRM lead
 * drawer, and the Kanban "cold rescue" flow. FAST mode = one-click grid
 * for bulk close-outs. GUIDED mode = category → reason → notes (+ next
 * action date for callback_scheduled).
 *
 * Backed by POST /api/crm/dispositions with target_type +
 * target_id + code.
 */
import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import {
    X, ThumbsUp, ThumbsDown, PhoneSlash, ClockCounterClockwise, Warning,
    UserMinus, GitFork, Prohibit, Buildings, CurrencyInr, MapPin, PuzzlePiece,
    Snowflake, ChatCircle, Handshake, Lightning, ListChecks,
} from "@phosphor-icons/react";

const CODES = [
    // CRM funnel codes (V2.1.0 iter167 — conversion focus, not categorization)
    { code: "reachable",              label: "Reachable",              icon: ThumbsUp,               tone: "emerald", category: "positive",  surface: "crm_lead",  hint: "Made contact. Next: send magic link or ask for CV." },
    { code: "magic_link_sent",        label: "Magic link sent",        icon: Lightning,              tone: "cobalt",  category: "positive",  surface: "crm_lead",  hint: "Registration link out. Waiting on candidate action." },
    { code: "registered",             label: "Registered",             icon: ThumbsUp,               tone: "emerald", category: "positive",  surface: "crm_lead",  hint: "Candidate registered. Promote to pool next." },
    { code: "not_reachable",          label: "Not reachable",          icon: PhoneSlash,             tone: "amber",   category: "retry",     surface: "both",      hint: "Attempt++ · try again later." },
    { code: "callback_scheduled",     label: "Callback",               icon: ClockCounterClockwise,  tone: "cobalt",  category: "positive",  surface: "both",      hint: "Set a date + notes below.", needsDate: true },
    // Candidate-side (Kanban / cold rescue) codes
    { code: "interested",             label: "Interested",             icon: ThumbsUp,               tone: "emerald", category: "positive",  surface: "candidate", hint: "Positive intent from candidate." },
    { code: "referral_pending",       label: "Referral pending",       icon: ChatCircle,             tone: "cobalt",  category: "positive",  surface: "candidate", hint: "Waiting on a referral — 14d cooldown." },
    { code: "offer_pending_elsewhere",label: "Offer pending",          icon: Handshake,              tone: "amber",   category: "retry",     surface: "candidate", hint: "Another offer in flight — 21d cooldown." },
    { code: "salary_mismatch",        label: "Salary mismatch",        icon: CurrencyInr,            tone: "amber",   category: "match",     surface: "candidate", hint: "Auto-resurfaces on matching-CTC role." },
    { code: "location_mismatch",      label: "Location mismatch",      icon: MapPin,                 tone: "amber",   category: "match",     surface: "candidate", hint: "Auto-resurfaces on matching-metro role." },
    { code: "profile_mismatch",       label: "Profile mismatch",       icon: PuzzlePiece,            tone: "amber",   category: "match",     surface: "candidate", hint: "Auto-resurfaces on skill graph match." },
    { code: "not_interested",         label: "Not interested",         icon: ThumbsDown,             tone: "rose",    category: "closed",    surface: "candidate", hint: "90d cooldown, then re-eligible." },
    { code: "cold_recycled",          label: "Cold — recycle",         icon: Snowflake,              tone: "rose",    category: "closed",    surface: "candidate", hint: "Parked · 90d cooldown." },
    { code: "hired_elsewhere",        label: "Hired elsewhere",        icon: Buildings,              tone: "rose",    category: "closed",    surface: "candidate", hint: "Note for future re-approach · 180d." },
    // Terminal codes valid on both surfaces
    { code: "wrong_contact",          label: "Wrong contact",          icon: Warning,                tone: "neutral", category: "suppress",  surface: "both",      hint: "Adds to suppression list." },
    { code: "duplicate",              label: "Duplicate",              icon: GitFork,                tone: "neutral", category: "suppress",  surface: "both",      hint: "Links to canonical row." },
    { code: "dnc",                    label: "Do not contact",         icon: Prohibit,               tone: "neutral", category: "suppress",  surface: "both",      hint: "Never contact again." },
];

const TONE_CLASSES = {
    emerald: "border-emerald-500 hover:bg-emerald-50 text-emerald-800",
    cobalt:  "border-cobalt      hover:bg-cobalt/5   text-cobalt",
    amber:   "border-amber-500   hover:bg-amber-50   text-amber-800",
    rose:    "border-rose-500    hover:bg-rose-50    text-rose-800",
    neutral: "border-neutral-400 hover:bg-neutral-50 text-neutral-700",
};

const CATEGORIES = [
    { key: "positive",  label: "Positive",       icon: ThumbsUp },
    { key: "retry",     label: "Retry / hold",   icon: ClockCounterClockwise },
    { key: "match",     label: "Mismatch",       icon: PuzzlePiece },
    { key: "closed",    label: "Close out",      icon: ThumbsDown },
    { key: "suppress",  label: "Suppress",       icon: Prohibit },
];

export default function DispositionModal({
    open,
    onClose,
    targetType,           // "crm_lead" | "candidate"
    targetId,
    onSubmitted,
    initialMode = "fast", // "fast" | "guided"
}) {
    const [mode, setMode] = useState(initialMode);
    const [category, setCategory] = useState(null);
    const [chosen, setChosen] = useState(null);
    const [notes, setNotes] = useState("");
    const [nextAt, setNextAt] = useState("");
    const [saving, setSaving] = useState(false);

    const chosenSpec = useMemo(() => CODES.find((c) => c.code === chosen), [chosen]);
    // V2.1.0 iter167 — filter CODES to the current surface. CRM is a
    // conversion funnel; the full 14-code categorization only applies
    // to candidates in the Kanban.
    const surfaceCodes = useMemo(() => {
        return CODES.filter((c) => c.surface === "both" || (targetType === "crm_lead" ? c.surface === "crm_lead" : c.surface === "candidate"));
    }, [targetType]);
    const gridByCategory = useMemo(() => {
        if (!category) return [];
        return surfaceCodes.filter((c) => c.category === category);
    }, [category, surfaceCodes]);

    if (!open) return null;

    const reset = () => {
        setCategory(null); setChosen(null); setNotes(""); setNextAt("");
    };
    const close = () => { reset(); onClose?.(); };

    async function submit(codeOverride) {
        const code = codeOverride || chosen;
        if (!code) return;
        const spec = surfaceCodes.find((c) => c.code === code) || CODES.find((c) => c.code === code);
        // fast-mode callback still needs a date; pop into guided in that case
        if (spec?.needsDate && !nextAt) {
            setMode("guided");
            setCategory(spec.category);
            setChosen(spec.code);
            return;
        }
        setSaving(true);
        try {
            const r = await api.post("/crm/dispositions", {
                target_type: targetType, target_id: targetId,
                code, reason_notes: notes || null,
                next_action_at: nextAt || null,
            });
            toast.success(`Marked as ${spec.label}`);
            onSubmitted?.(r.data);
            close();
        } catch (e) {
            logError("Disposition submit failed:", e);
            toast.error(e?.response?.data?.detail || "Couldn't save disposition");
        } finally { setSaving(false); }
    }

    return (
        <div
            data-testid="disposition-modal"
            className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={close}
        >
            <div
                className="bg-white border-2 border-ink w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-ink">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                            Disposition · {targetType === "crm_lead" ? "CRM Lead" : "Candidate"}
                        </p>
                        <h2 className="font-heading font-black text-2xl leading-none mt-1">
                            Close the loop
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex border border-ink" data-testid="disposition-mode-toggle">
                            <button
                                type="button"
                                data-testid="disposition-mode-fast"
                                onClick={() => { setMode("fast"); reset(); }}
                                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1 ${mode === "fast" ? "bg-ink text-white" : "text-ink"}`}
                            >
                                <Lightning size={12} weight="bold" /> Fast
                            </button>
                            <button
                                type="button"
                                data-testid="disposition-mode-guided"
                                onClick={() => setMode("guided")}
                                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1 ${mode === "guided" ? "bg-ink text-white" : "text-ink"}`}
                            >
                                <ListChecks size={12} weight="bold" /> Guided
                            </button>
                        </div>
                        <button data-testid="disposition-close" onClick={close} className="p-1 hover:bg-neutral-100">
                            <X size={18} weight="bold" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                    {mode === "fast" ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="disposition-fast-grid">
                            {surfaceCodes.map((c) => {
                                const Icon = c.icon;
                                return (
                                    <button
                                        key={c.code}
                                        type="button"
                                        data-testid={`disposition-code-${c.code}`}
                                        disabled={saving}
                                        onClick={() => submit(c.code)}
                                        className={`border-2 ${TONE_CLASSES[c.tone]} p-3 text-left transition-colors disabled:opacity-50`}
                                        title={c.hint}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Icon size={16} weight="bold" />
                                            <span className="text-xs font-bold leading-tight">{c.label}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="disposition-guided">
                            {/* Step 1 categories */}
                            <div>
                                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Step 1 · Category</p>
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    {CATEGORIES.map((cat) => {
                                        const Icon = cat.icon;
                                        const active = category === cat.key;
                                        return (
                                            <button
                                                key={cat.key}
                                                type="button"
                                                data-testid={`disposition-cat-${cat.key}`}
                                                onClick={() => { setCategory(cat.key); setChosen(null); }}
                                                className={`border-2 p-2 flex flex-col items-center justify-center gap-1 transition-colors ${active ? "border-ink bg-ink text-white" : "border-neutral-300 hover:border-ink text-ink"}`}
                                            >
                                                <Icon size={16} weight="bold" />
                                                <span className="text-[10px] font-bold uppercase tracking-wider">{cat.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* Step 2 codes */}
                            {category && (
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Step 2 · Reason</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {gridByCategory.map((c) => {
                                            const Icon = c.icon;
                                            const active = chosen === c.code;
                                            return (
                                                <button
                                                    key={c.code}
                                                    type="button"
                                                    data-testid={`disposition-guided-code-${c.code}`}
                                                    onClick={() => setChosen(c.code)}
                                                    className={`border-2 p-3 text-left transition-colors ${active ? "border-ink bg-ink/5" : `border-neutral-300 hover:${TONE_CLASSES[c.tone].split(" ")[0]}`}`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Icon size={16} weight="bold" />
                                                        <span className="text-sm font-bold">{c.label}</span>
                                                    </div>
                                                    <p className="text-[11px] text-neutral-600 mt-1">{c.hint}</p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {/* Step 3 optional */}
                            {chosen && (
                                <div className="space-y-3">
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Step 3 · Details</p>
                                    {chosenSpec?.needsDate && (
                                        <label className="block">
                                            <span className="text-xs font-bold">Callback date &amp; time</span>
                                            <input
                                                type="datetime-local"
                                                value={nextAt}
                                                onChange={(e) => setNextAt(e.target.value)}
                                                data-testid="disposition-next-at"
                                                className="mt-1 block w-full border-2 border-ink px-3 py-2 text-sm"
                                                required
                                            />
                                        </label>
                                    )}
                                    <label className="block">
                                        <span className="text-xs font-bold">Notes (optional)</span>
                                        <textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            data-testid="disposition-notes"
                                            rows={3}
                                            maxLength={2000}
                                            placeholder="Short context that will help you or the next recruiter…"
                                            className="mt-1 block w-full border-2 border-ink px-3 py-2 text-sm"
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer (guided mode only) */}
                {mode === "guided" && (
                    <div className="flex items-center justify-between px-6 py-4 border-t-2 border-ink">
                        <span className="text-xs text-neutral-500">
                            {chosen ? `Ready to save: ${chosenSpec?.label}` : "Pick a category and a reason."}
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={close}
                                data-testid="disposition-cancel"
                                className="border-2 border-ink px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-neutral-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => submit()}
                                disabled={!chosen || saving || (chosenSpec?.needsDate && !nextAt)}
                                data-testid="disposition-save"
                                className="bg-ink text-white px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-40"
                            >
                                {saving ? "Saving…" : "Save disposition"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
